import { useState } from 'react';

import { useToastStore } from '../../../stores/toastStore';
import type { CreateLicensePlanRequest, LicensePlanDefinitionResponse, UpdateLicensePlanRequest } from '../api';
import { EditPlanModal } from '../components/EditPlanModal';
import { PlanBadge } from '../components/PlanBadge';
import { useCreateLicensePlan, useDeleteLicensePlan, useLicensePlans, useUpdateLicensePlan } from '../hooks/useLicensePlans';

export function LicensePlansPage(): JSX.Element {
  const { data: plans, isLoading, isError } = useLicensePlans();
  const updateMutation = useUpdateLicensePlan();
  const createMutation = useCreateLicensePlan();
  const deleteMutation = useDeleteLicensePlan();
  const pushToast = useToastStore((s) => s.push);
  const [editingPlan, setEditingPlan] = useState<LicensePlanDefinitionResponse | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const handleSave = (licenseType: string, request: UpdateLicensePlanRequest): void => {
    updateMutation.mutate(
      { licenseType, request },
      {
        onSuccess: () => { pushToast({ tone: 'success', title: 'Plan actualizado' }); setEditingPlan(null); },
        onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
      },
    );
  };

  const handleCreate = (request: CreateLicensePlanRequest): void => {
    createMutation.mutate(request, {
      onSuccess: () => { pushToast({ tone: 'success', title: 'Plan creado' }); setCreateOpen(false); },
      onError: (err) => pushToast({ tone: 'error', title: 'Error al crear plan', description: err.message }),
    });
  };

  const handleDelete = (plan: LicensePlanDefinitionResponse): void => {
    if (!confirm(`¿Eliminar el plan "${plan.displayName}"? Esta acción no se puede deshacer.`)) return;
    deleteMutation.mutate(plan.licenseType, {
      onSuccess: () => pushToast({ tone: 'success', title: 'Plan eliminado' }),
      onError: (err) => pushToast({ tone: 'error', title: 'Error al eliminar', description: err.message }),
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Planes de licencia</h1>
          <p className="mt-1 text-sm text-slate-500">Define límites y costes de cada plan.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
          Nuevo plan
        </button>
      </header>

      {isError ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">No se pudieron cargar los planes.</div>
      ) : null}

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-36 animate-pulse rounded-xl bg-slate-100" />)}
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-3">
          {(plans ?? []).map((plan) => (
            <div key={plan.licenseType} className="card p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <PlanBadge plan={plan.licenseType} />
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${plan.isBuiltIn ? 'bg-slate-100 text-slate-500' : 'bg-sky-50 text-sky-700'}`}>
                    {plan.isBuiltIn ? 'Built-in' : 'Custom'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingPlan(plan)}
                    className="text-xs text-primary hover:underline"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(plan)}
                    disabled={plan.isBuiltIn || deleteMutation.isPending}
                    className="text-xs text-rose-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    title={plan.isBuiltIn ? 'Los planes integrados no se pueden eliminar' : 'Eliminar plan'}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
              <h2 className="mt-3 text-lg font-semibold text-slate-900">{plan.displayName}</h2>
              {plan.hasCost ? (
                <p className="text-sm text-slate-500">
                  {plan.monthlyCostAmount?.toLocaleString('es-ES', { style: 'currency', currency: plan.costCurrency })} / mes
                </p>
              ) : (
                <p className="text-sm text-slate-500">Gratuito</p>
              )}

              <dl className="mt-4 space-y-2 text-sm">
                <PlanLimit label="Documentos/mes" value={plan.documentsPerMonth === null ? '∞' : plan.documentsPerMonth.toLocaleString('es-ES')} />
                <PlanLimit label="Tipos de documento" value={plan.maxDocumentTypes === null ? "∞" : plan.maxDocumentTypes} />
                <PlanLimit label="Usuarios" value={plan.maxUsers === null ? "∞" : plan.maxUsers} />
                <PlanLimit label="Retención" value={`${plan.jobRetentionDays} días`} />
                <PlanLimit label="Providers locales" value={plan.allowsLocalAiProviders ? 'Sí' : 'No'} />
                <PlanLimit label="Webhooks" value={plan.allowsWebhooks ? 'Sí' : 'No'} />
              </dl>

              <div className="mt-3 text-xs text-slate-600">
                <span className="font-medium text-slate-500">OCR</span>
                {' · read: '}
                {plan.readCallsPerMonth === null ? '∞' : plan.readCallsPerMonth.toLocaleString('es-ES')}
                {' · layout: '}
                {plan.layoutCallsPerMonth === null ? '∞' : plan.layoutCallsPerMonth.toLocaleString('es-ES')}
                {' · document: '}
                {plan.documentCallsPerMonth === null ? '∞' : plan.documentCallsPerMonth.toLocaleString('es-ES')}
              </div>

              {plan.allowedPlatformProviders.length > 0 ? (
                <div className="mt-4">
                  <p className="text-xs font-medium text-slate-500">Platform providers</p>
                  <ul className="mt-1 space-y-1">
                    {plan.allowedPlatformProviders.map((p) => (
                      <li key={p.platformProviderId} className="text-xs text-slate-700">
                        {p.displayName ?? p.platformProviderId}
                        {p.monthlyCallLimit !== null ? ` · ${p.monthlyCallLimit.toLocaleString('es-ES')} llamadas/mes` : ' · Ilimitado'}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <EditPlanModal
        plan={editingPlan}
        busy={updateMutation.isPending}
        onClose={() => setEditingPlan(null)}
        onSubmit={handleSave}
      />

      <EditPlanModal
        plan={null}
        createMode={createOpen}
        busy={createMutation.isPending}
        onClose={() => setCreateOpen(false)}
        onSubmit={() => { /* no-op — handled by onCreate */ }}
        onCreate={handleCreate}
      />
    </div>
  );
}

function PlanLimit({ label, value }: { label: string; value: string | number }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-900">{value}</dd>
    </div>
  );
}
